import { useDispatch } from 'react-redux';

import styled from '@emotion/styled';
import { Box, Flex, Image, Text } from '@opendoor/bricks/core';
import { media } from '@opendoor/bricks/theme/novo';
import {
  RadioContainer,
  Input as RadioInput,
} from '@opendoor/cinderblocks/growth/novo/inputs/RadioInput';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { ImageSelectQuestion } from 'src/lib/flow/FlowQuestion';
import { changeAnswers } from 'src/store/sellerInput';

// TODO - bricks we don't have these border radii in our foundations for novo (20px and 22px)
const Label = styled(RadioContainer)`
  display: block;
  cursor: pointer;
  overflow: hidden;

  border-radius: 20px;
  ${media.largerThan.MD} {
    border-radius: 22px;
  }
  ${media.largerThan.LG} {
    border-radius: 24px;
  }
`.withComponent('label');

type ImageSelectProps = {
  description: string;
  imageUrl: string;
  isSelected: boolean;
  label: string;
  name: string;
  onChange: React.ChangeEventHandler;
  value: string;
};

const ImageSelect: React.FC<ImageSelectProps> = ({
  isSelected,
  label,
  imageUrl,
  value,
  description,
  onChange,
  name,
}) => {
  return (
    <Box position="relative">
      <RadioInput
        type="radio"
        id={`${name}-${value}`}
        name={name}
        value={value}
        checked={isSelected}
        onChange={onChange}
      />
      <Label htmlFor={`${name}-${value}`}>
        <Flex flexDir="column" gap="20px" minHeight={['224px', null, null, '275px']}>
          <Image
            objectFit="cover"
            height={['96px', null, null, '160px']}
            alt={label}
            src={imageUrl}
          />
          <Flex flex={1} flexDir="column" px={[6, null, null, 7]} pb={[6, null, null, 7]} gap="4px">
            <Text size="140" fontWeight="medium">
              {label}
            </Text>
            <Text size="100">{description}</Text>
          </Flex>
        </Flex>
      </Label>
    </Box>
  );
};

type ImageSelectInputProps = {
  question: ImageSelectQuestion;
  value?: string;
};

const ImageSelectInput: React.FC<ImageSelectInputProps> = ({ question, value }) => {
  const { options, key: name } = question;
  const dispatch = useDispatch();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(changeAnswers([{ key: name, answer: value ?? null }]));
    trackAnswerChange(name, value);
  };

  const selected = value ?? null;
  return (
    <Flex
      className="image-select"
      display="grid"
      gridTemplateColumns="1fr 1fr"
      gap={['4', null, null, '5']}
      role="radiogroup"
    >
      {options.map(({ value, label, description, imageUrl }) => {
        const isSelected = selected === value;
        return (
          <ImageSelect
            name={name}
            isSelected={isSelected}
            key={`option-${value}`}
            value={value as string}
            description={description}
            label={label}
            imageUrl={imageUrl}
            onChange={onChange}
          />
        );
      })}
    </Flex>
  );
};

export default ImageSelectInput;
